import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight } from "lucide-react";

const RevealLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ delay, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

function CoachCard3D() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div className="relative w-full max-w-[340px] sm:max-w-md mx-auto lg:ml-auto" style={{ perspective: 1200 }}>
      {/* Parola subliminale dietro la foto */}
      <motion.span
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1.4 }}
        className="hero-ghost-word font-display font-black uppercase absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none pointer-events-none whitespace-nowrap leading-none tracking-tight"
      >
        Disciplina
      </motion.span>

      <motion.div
        data-testid="hero-photo-3d"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}
        animate={{ rotateZ: [-0.6, 0.6, -0.6] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        className="relative"
      >
        <div
          aria-hidden="true"
          className="absolute left-1/2 top-[58%] -translate-x-1/2 -translate-y-1/2 w-[70%] aspect-square rounded-full bg-blood/30 blur-3xl"
          style={{ transform: "translate(-50%, -50%) translateZ(-120px)" }}
        />
        <div
          aria-hidden="true"
          className="absolute left-1/2 bottom-0 -translate-x-1/2 w-[85%] h-10 rounded-full bg-black/80 blur-xl"
          style={{ transform: "translateX(-50%) translateZ(-40px)" }}
        />
        <img
          src="/coach-hero-cutout.webp"
          alt="Coach DisciplinaFit — il dito alla testa: la disciplina parte dalla mente"
          data-testid="hero-coach-photo"
          className="relative w-full aspect-[850/1193] object-contain object-bottom drop-shadow-[0_30px_50px_rgba(0,0,0,0.85)]"
          style={{ transform: "translateZ(40px)" }}
          fetchpriority="high"
        />
      </motion.div>
    </div>
  );
}

export default function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-center pt-24 pb-14 lg:pb-16 px-5 sm:px-8 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 78% 40%, rgba(229,9,20,0.16), transparent 65%), radial-gradient(ellipse 40% 35% at 10% 90%, rgba(229,9,20,0.08), transparent 60%)",
        }}
      />
      <span aria-hidden="true" className="text-stroke font-display font-black uppercase absolute top-16 left-0 text-[24vw] leading-none tracking-tight select-none pointer-events-none whitespace-nowrap">
        Disciplina
      </span>

      <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-12 gap-8 lg:gap-8 items-center relative z-10">
        <h1
          data-testid="hero-phrase"
          className="lg:col-span-7 font-display font-black uppercase leading-[0.9] text-5xl sm:text-7xl lg:text-8xl xl:text-9xl tracking-tight"
        >
          <RevealLine delay={0.2}>
            La <span className="text-blood">disciplina</span>
          </RevealLine>
          <RevealLine delay={0.36}>batte</RevealLine>
          <RevealLine delay={0.52}>il talento</RevealLine>
        </h1>

        <motion.div
          style={{ y: imgY }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative"
          data-testid="hero-photo-wrap"
        >
          <CoachCard3D />

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9 lg:mt-10 w-full max-w-[340px] sm:max-w-md mx-auto lg:ml-auto"
          >
            <a
              href="#pacchetti"
              data-testid="hero-start-now-btn"
              className="group flex items-center justify-between gap-4 w-full bg-blood hover:bg-blood-hot text-white font-display font-bold uppercase tracking-wider text-lg sm:text-xl px-6 py-4 transition-colors red-glow"
            >
              <span>Inizia ora</span>
              <ArrowRight size={22} className="transition-transform group-hover:translate-x-1.5" />
            </a>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        aria-hidden="true"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-blood hidden lg:block"
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M12 4v16M5 13l7 7 7-7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </motion.div>
    </section>
  );
}
